import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaUser, FaEnvelope, FaFileInvoice, FaMoneyBillWave } from 'react-icons/fa';
import { formatDate } from '../../utils/formatting';

const VendorDetailModal = ({ vendor, onClose }) => {
  const { t } = useTranslation();



  const formatCurrency = (amount) => {
    return `${(amount || 0).toLocaleString()} ${t('common.currency')}`;
  };

  const getPaymentTermsLabel = (terms) => {
    const termsMap = {
      'due_on_receipt': t('accounting.due_on_receipt'),
      'net_10': t('accounting.net_10'),
      'net_15': t('accounting.net_15'),
      'net_30': t('accounting.net_30'),
      'net_60': t('accounting.net_60'),
      'net_90': t('accounting.net_90')
    };
    return termsMap[terms] || terms;
  };

  const getVendorTypeLabel = (type) => {
    const typeMap = {
      'supplier': t('common.supplier'),
      'service_provider': t('common.service_provider'),
      'contractor': t('common.contractor'),
      'consultant': t('common.consultant'),
      'other': t('common.other')
    };
    return typeMap[type] || type;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">{t('accounting.vendor_details')}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FaUser className="w-5 h-5 text-blue-600" />
              {t('common.basic_info')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('vendors.vendor_name')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.name || '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('clients.company_name')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.companyName || '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('vendors.vendor_type')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.vendorCode || '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('accounting.vendor_type')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {getVendorTypeLabel(vendor.vendorType)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.status')}
                </label>
                <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${vendor.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
                  }`}>
                  {vendor.status === 'active' ? t('common.active') : t('common.inactive')}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.created')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {formatDate(vendor.createdAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FaEnvelope className="w-5 h-5 text-blue-600" />
              {t('common.contact_info')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('clients.email')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.email || '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('clients.phone')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.phone || '-'}
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('clients.address')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.address || '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('clients.city')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.city || '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('clients.country')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.country || '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('accounting.vendor_tax_id')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.taxId || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Contact Person */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FaUser className="w-5 h-5 text-blue-600" />
              {t('accounting.vendor_contact')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('clients.contact_person')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.contactPerson || '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.contact_phone')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.contactPhone || '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.contact_email')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {vendor.contactEmail || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FaMoneyBillWave className="w-5 h-5 text-blue-600" />
              {t('common.financial_info')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('vendors.payment_terms')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {getPaymentTermsLabel(vendor.paymentTerms)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('accounting.vendor_credit_limit')}
                </label>
                <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                  {formatCurrency(vendor.creditLimit)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('vendors.outstanding_amount')}
                </label>
                <p className={`bg-white px-3 py-2 rounded border font-medium ${(vendor.balance || vendor.outstanding || 0) > 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                  {formatCurrency(Math.abs(vendor.balance || vendor.outstanding || 0))}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('accounting.vendor_outstanding')}
                </label>
                <p className="text-orange-600 bg-white px-3 py-2 rounded border font-medium">
                  {formatCurrency(vendor.outstanding)}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {vendor.notes && (
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaFileInvoice className="w-5 h-5 text-blue-600" />
                {t('clients.notes')}
              </h3>
              <p className="text-gray-900 bg-white px-3 py-2 rounded border">
                {vendor.notes}
              </p>
            </div>
          )}

          {/* Summary Statistics */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('accounting.vendor_summary')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {vendor.totalBills || vendor.billCount || 0}
                </div>
                <div className="text-sm text-gray-600">{t('accounting.vendor_bills')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {vendor.totalPayments || vendor.paymentCount || 0}
                </div>
                <div className="text-sm text-gray-600">{t('accounting.vendor_payments')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {formatCurrency(vendor.totalSpent || vendor.totalBilled || 0)}
                </div>
                <div className="text-sm text-gray-600">{t('common.total_spent')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorDetailModal; 